import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./document-krookie.module.scss";
import closeIcon from "../../images/master-plan-close.svg";
import { useNavigate } from "react-router-dom";
import {
  CardKrookies,
  ChangeKrookieStatusPayload,
  DocumentOrigin,
  KrookieBackendPayload,
  KrookieStatus,
  KrookieStatusLabels,
} from "../../interfaces/krookie";
import { useAppSelector } from "../../hooks/useAppSelector";
import useGeneratePdf from "../../hooks/useGeneratePdf";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import {
  fetchKrookieData,
  fetchMasterPlanModificationData,
  FormationTypeNames,
  KrookieFormationTypeId,
  setDrafter,
  setKrookiePlots,
  setResetKrookieData,
  setSelectedCity,
  setSelectedFormationType,
  setSelectedPlots,
  updateConvertedKrookieDataAsync,
} from "../../services/reducers/master-plan-modification";
import { LandPlot } from "../../interfaces/card";
import { changeKrookieStatus, GetKrookieDoc } from "../../services/api";
import {
  addDividedPlotToArcigs,
  addMergedPlotToArcgis,
  addPlotFromKrookieDataToArcgis,
  buildRevision,
  createLandPlot,
  createMergedKrookiePlot,
  deletePlotsFromArcgis,
  updatePlotFromKrookieData,
} from "../../utils/krookie-helpers";
import { fetchKrookies } from "../../services/reducers/table";
import { fetchAllArcgisObjects } from "../../services/reducers/filters";
import CardKrookie from "./card-krookie/card-krookie";
import HistoryLog from "./history-log/history-log";
import KrookieActionPanel from "./krookie-action-panel/krookie-action-panel";
import Polygon from "@arcgis/core/geometry/Polygon";
import { Roles } from "../../types/roles";
import warningIcon from "../../images/warning.svg";
import {
  appendKrookiesData,
  appendOldPlots,
  getStatusIcon,
  prepareKrookieApproveData,
  prepareKrookieData,
  processSaveResponse,
  updateKrookiesWithLatestComments,
} from "./krookie-handlers";

interface DocumentKrookie {
  setVisibleDocumentKrookie: (visible: boolean) => void;
  setVisibleModalSuccessful?: (visible: boolean) => void;
  setVisibleModalRejectKrookie?: (visible: boolean) => void;
  setVisibleModalApproveKrookie?: (visible: boolean) => void;
  data: Record<number, CardKrookies>;
  status: KrookieStatus;
  origin: DocumentOrigin;
  krookieFormationTypeId: KrookieFormationTypeId;
  onToggleCloseMasterPlanModal?: () => void;
}

const DocumentKrookie: FC<DocumentKrookie> = ({
  setVisibleDocumentKrookie,
  setVisibleModalSuccessful,
  setVisibleModalApproveKrookie,
  setVisibleModalRejectKrookie,
  data,
  origin,
  status,
  krookieFormationTypeId,
  onToggleCloseMasterPlanModal,
}) => {
  const { t } = useTranslation();
  const { krookiePlots, logComment, convertedKrookieData, selectedPlots } =
    useAppSelector((state) => state.masterPlanModification);
  const plotsData = useAppSelector(
    (state) => state.masterPlanModification.data,
  );
  const { user } = useAppSelector((state) => state.user);
  const { layers } = useAppSelector((state) => state.map);

  const generatePdf = useGeneratePdf();
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [currentKrookieIndex, setCurrentKrookieIndex] = useState(0);
  const [savedKrookiesData, setSavedKrookiesData] = useState<
    KrookieBackendPayload[]
  >([]);
  const [savedKrookieKeys, setSavedKrookieKeys] = useState<Set<number>>(
    new Set(),
  );
  const [approvedKrookiesData, setApprovedKrookiesData] = useState<
    Map<number, ChangeKrookieStatusPayload>
  >(new Map());
  const [hasCriticalOperation, setHasCriticalOperation] = useState(false);

  const allKrookies = Object.values(data);
  const allKrookieKeys = Object.keys(data).map(Number);
  const currentKrookie = allKrookies[currentKrookieIndex];
  const currentKrookieKey = allKrookieKeys[currentKrookieIndex];
  const currentConvertedKrookieData =
    convertedKrookieData.krookies[currentKrookieKey];
  const hasMultipleKrookies = allKrookies.length > 1;
  const isCurrentKrookieSaved = savedKrookieKeys.has(currentKrookieKey);
  const unsavedKrookiesCount = allKrookieKeys.filter(
    (key) => !savedKrookieKeys.has(key),
  ).length;
  const isLastUnsavedKrookie =
    unsavedKrookiesCount === 1 && !isCurrentKrookieSaved;
  const currentDocumentUrl = currentConvertedKrookieData?.documentImage ?? "";
  const currentKrookiePlot = krookiePlots.find(
    (plot) => plot.objectId === currentKrookieKey,
  );
  const showApprovalAlert =
    status === KrookieStatus.NeedApproval &&
    user?.roles.includes(Roles.KrookieApprover) &&
    hasMultipleKrookies;

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasCriticalOperation) {
        event.preventDefault();
        return true;
      }
    };

    if (hasCriticalOperation) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasCriticalOperation]);

  const clearKrookieStates = () => {
    setSavedKrookieKeys(new Set());
    setApprovedKrookiesData(new Map());
    setSavedKrookiesData([]);
  };

  const handleSaveKrookieAndSeeNext = async (
    statusId: KrookieStatus | undefined = undefined,
  ) => {
    const krookieData = await prepareKrookieData(
      statusId,
      pdfContainerRef.current,
      generatePdf,
      currentKrookie,
      currentConvertedKrookieData,
      logComment,
      currentKrookiePlot,
      currentKrookieKey,
    );
    if (krookieData) {
      setSavedKrookiesData((prev) => {
        const newData = [...prev];
        newData[currentKrookieIndex] = krookieData;
        return newData;
      });
      setSavedKrookieKeys((prev) => new Set(prev).add(currentKrookieKey));
      navigateKrookie("next");
    }
  };

  const handleNewKrookie = async (
    formData: FormData,
    krookiesDataToBackEnd: KrookieBackendPayload[],
    krookieFormationTypeId: KrookieFormationTypeId,
  ) => {
    formData.append(
      "KrookieFormationTypeId",
      krookieFormationTypeId.toString(),
    );
    appendKrookiesData(formData, krookiesDataToBackEnd);
    appendOldPlots(formData, selectedPlots);
    await processSaveResponse(
      formData,
      setVisibleDocumentKrookie,
      setVisibleModalSuccessful,
    );
  };

  const handleUpdateKrookie = async (
    formData: FormData,
    krookiesDataToBackEnd: KrookieBackendPayload[],
  ) => {
    if (!currentConvertedKrookieData) return;
    formData.append("Id", currentConvertedKrookieData.sessionId.toString());
    appendKrookiesData(formData, krookiesDataToBackEnd);
    await processSaveResponse(
      formData,
      setVisibleDocumentKrookie,
      setVisibleModalSuccessful,
    );
  };

  const handleSaveKrookie = async (
    statusId: KrookieStatus | undefined = undefined,
  ) => {
    const currentKrookieData = await prepareKrookieData(
      statusId,
      pdfContainerRef.current,
      generatePdf,
      currentKrookie,
      currentConvertedKrookieData,
      logComment,
      currentKrookiePlot,
      currentKrookieKey,
    );
    if (!currentKrookieData) return;

    const allKrookiesData = [...savedKrookiesData];
    allKrookiesData[currentKrookieIndex] = currentKrookieData;

    const updatedKrookiesData = updateKrookiesWithLatestComments(
      allKrookiesData,
      allKrookieKeys,
      logComment,
    );

    const formData = new FormData();

    if (!currentConvertedKrookieData) {
      await handleNewKrookie(
        formData,
        updatedKrookiesData,
        krookieFormationTypeId,
      );
    } else {
      await handleUpdateKrookie(formData, updatedKrookiesData);
    }

    clearKrookieStates();
  };

  const handlePrintKrookie = async () => {
    await GetKrookieDoc(currentConvertedKrookieData.id);
  };

  const handleReturnBackToJournal = () => {
    setVisibleDocumentKrookie(false);
    if (origin === DocumentOrigin.Journal) {
      dispatch(setResetKrookieData());
    }
  };

  const handleEditKrookie = () => {
    if (!convertedKrookieData) return;
    const selectedPlots: LandPlot[] = [];

    if (convertedKrookieData.oldPlots.length > 0) {
      convertedKrookieData.oldPlots.forEach((oldPlot) => {
        const foundPlot = plotsData.find(
          (plot: LandPlot) => plot.objectId === oldPlot.plotId,
        );
        if (foundPlot) {
          selectedPlots.push(foundPlot);
        }
      });
    }

    switch (krookieFormationTypeId) {
      case KrookieFormationTypeId.Create: {
        const objectId = Number(Object.keys(convertedKrookieData.krookies)[0]);
        const baseEmptyPlot = createLandPlot({
          geometry: currentConvertedKrookieData.geometry,
          number: currentConvertedKrookieData.plotNumber,
          estateEn: currentConvertedKrookieData.cityEn.value ?? 0,
          estateAr: currentConvertedKrookieData.cityAr,
          landUse: currentConvertedKrookieData.landUse.value ?? 0,
          landUseId: currentConvertedKrookieData.landUse.value ?? 0,
          phase: currentConvertedKrookieData.phase.value ?? 0,
          phaseId: currentConvertedKrookieData.phase.value ?? 0,
          area: currentConvertedKrookieData.area,
          objectId,
        });

        dispatch(setKrookiePlots([baseEmptyPlot]));
        break;
      }

      case KrookieFormationTypeId.Edit: {
        const lastOldPlot =
          convertedKrookieData.oldPlots[
            convertedKrookieData.oldPlots.length - 1
          ];
        const foundPlot: LandPlot = plotsData.find(
          (plot: LandPlot) => plot.objectId === lastOldPlot.plotId,
        );
        const objectId = Number(Object.keys(convertedKrookieData.krookies)[0]);
        if (foundPlot) {
          const editedPlot = {
            ...foundPlot,
            number: currentConvertedKrookieData.plotNumber,
            geometry: new Polygon(currentConvertedKrookieData.geometry),
            objectId,
          };
          dispatch(setKrookiePlots([editedPlot]));
        }
        break;
      }

      case KrookieFormationTypeId.Merge: {
        const baseMergedPlot = createMergedKrookiePlot(selectedPlots);
        const objectId = Number(Object.keys(convertedKrookieData.krookies)[0]);

        if (baseMergedPlot) {
          const mergedPlot = {
            ...baseMergedPlot,
            number: currentConvertedKrookieData.plotNumber,
            objectId,
            geometry: currentConvertedKrookieData.geometry,
            estateEn: currentConvertedKrookieData.cityEn.value ?? 0,
            estateAr: currentConvertedKrookieData.cityAr,
            landUse: currentConvertedKrookieData.landUse.value ?? 0,
            landUseId: currentConvertedKrookieData.landUse.value ?? 0,
            phase: currentConvertedKrookieData.phase.value ?? 0,
            phaseId: currentConvertedKrookieData.phase.value ?? 0,
            area: currentConvertedKrookieData.area,
          };
          dispatch(setKrookiePlots([mergedPlot]));
        }
        break;
      }

      case KrookieFormationTypeId.Divide: {
        const dividedPlots: LandPlot[] = [];

        Object.entries(convertedKrookieData.krookies).forEach(
          ([objectId, krookieData]) => {
            const baseDividedPlot = createLandPlot({
              geometry: krookieData.geometry,
              number: krookieData.plotNumber,
              estateEn: krookieData.cityEn.value ?? 0,
              estateAr: krookieData.cityAr,
              landUse: krookieData.landUse.value ?? 0,
              landUseId: krookieData.landUse.value ?? 0,
              phase: krookieData.phase.value ?? 0,
              phaseId: krookieData.phase.value ?? 0,
              area: krookieData.area,
              objectId: Number(objectId),
            });
            dividedPlots.push(baseDividedPlot);
          },
        );

        dispatch(setKrookiePlots(dividedPlots));
        break;
      }
    }

    if (selectedPlots.length > 0) {
      dispatch(setSelectedPlots(selectedPlots));
    }
    dispatch(setSelectedCity(currentConvertedKrookieData.cityEn.value));
    dispatch(setDrafter({ name: user?.userName || "", date: new Date() }));

    switch (krookieFormationTypeId) {
      case KrookieFormationTypeId.Create: {
        dispatch(setSelectedFormationType(FormationTypeNames.Create));
        navigate("/masterPlan/create");
        break;
      }

      case KrookieFormationTypeId.Edit: {
        dispatch(setSelectedFormationType(FormationTypeNames.Edit));
        navigate("/masterPlan/edit");
        break;
      }

      case KrookieFormationTypeId.Merge: {
        dispatch(setSelectedFormationType(FormationTypeNames.Merge));
        navigate("/masterPlan/merge");
        break;
      }

      case KrookieFormationTypeId.Divide: {
        dispatch(setSelectedFormationType(FormationTypeNames.Divide));
        navigate("/masterPlan/divide");
        break;
      }
    }
  };

  const handleApproveKrookieAndSeeNext = async () => {
    if (!convertedKrookieData || !currentConvertedKrookieData) return;

    const approvalData = await prepareKrookieApproveData(
      currentKrookieKey,
      currentConvertedKrookieData,
      user,
      dispatch,
      pdfContainerRef.current,
      generatePdf,
    );
    if (!approvalData) {
      return;
    }

    setApprovedKrookiesData((prev) => {
      const newMap = new Map(prev);
      newMap.set(currentKrookieKey, approvalData);
      return newMap;
    });

    setSavedKrookieKeys((prev) => new Set(prev).add(currentKrookieKey));
    navigateKrookie("next");
  };

  const handleApproveKrookie = async () => {
    if (!convertedKrookieData) return;

    setHasCriticalOperation(true);

    const localApprovalData = new Map(approvedKrookiesData);
    const isCreate = krookieFormationTypeId === KrookieFormationTypeId.Create;
    const isMerge = krookieFormationTypeId === KrookieFormationTypeId.Merge;
    const isDivide = krookieFormationTypeId === KrookieFormationTypeId.Divide;

    try {
      let currentApprovalData = localApprovalData.get(currentKrookieKey);
      if (!currentApprovalData) {
        currentApprovalData = await prepareKrookieApproveData(
          currentKrookieKey,
          currentConvertedKrookieData,
          user,
          dispatch,
          pdfContainerRef.current,
          generatePdf,
        );
        if (!currentApprovalData) return;
        localApprovalData.set(currentKrookieKey, currentApprovalData);
      }

      const oldPlots = convertedKrookieData.oldPlots;

      let deleteResult;
      if (isDivide || isMerge) {
        const oldPlotsIds = oldPlots.map((plot) => plot.plotId);
        deleteResult = await deletePlotsFromArcgis(layers, oldPlotsIds);
      }

      for (const krookieKey of allKrookieKeys) {
        const currentKrookie = convertedKrookieData.krookies[krookieKey];

        let newObjectId: number | null = null;

        if (isCreate) {
          newObjectId = await addPlotFromKrookieDataToArcgis(
            layers,
            currentKrookie,
          );
        } else if (isMerge && deleteResult) {
          newObjectId = await addMergedPlotToArcgis(
            currentKrookie,
            deleteResult,
          );
        } else if (isDivide && deleteResult) {
          newObjectId = await addDividedPlotToArcigs(
            currentKrookie,
            deleteResult,
          );
        } else {
          await updatePlotFromKrookieData(layers, currentKrookie, oldPlots);
        }

        if (newObjectId) {
          const existingData = localApprovalData.get(krookieKey);
          if (existingData) {
            localApprovalData.set(krookieKey, {
              ...existingData,
              newPlotId: newObjectId.toString(),
            });
          }
        }
      }

      const finalApprovalData = Array.from(localApprovalData.values()).map(
        (approvalData) => ({
          krookieId: approvalData.krookieId,
          changeNum: approvalData.changeNum,
          documentBody: approvalData.documentBody,
          ...(approvalData.newPlotId && { newPlotId: approvalData.newPlotId }),
        }),
      );

      await changeKrookieStatus(KrookieStatus.Approved, finalApprovalData);

      await dispatch(
        fetchKrookieData({
          sessionId: currentConvertedKrookieData.sessionId,
          status: KrookieStatusLabels[KrookieStatus.Approved],
          krookieId: currentConvertedKrookieData.id,
        }),
      );

      await Promise.all([
        dispatch(fetchAllArcgisObjects()),
        dispatch(fetchMasterPlanModificationData()),
      ]);

      setVisibleModalApproveKrookie?.(true);
      clearKrookieStates();
      dispatch(fetchKrookies());
    } catch (error) {
      console.error(error);
    } finally {
      setHasCriticalOperation(false);
    }
  };

  const handleSubmitForApprovalAndSeeNext = async () => {
    if (!convertedKrookieData) return;
    setSavedKrookieKeys((prev) => new Set(prev).add(currentKrookieKey));
    navigateKrookie("next");
  };

  const handleSubmitForApproval = async () => {
    if (!convertedKrookieData) return;

    try {
      const krookiesToSubmit = allKrookieKeys.filter(
        (key) => savedKrookieKeys.has(key) || key === currentKrookieKey,
      );

      if (!savedKrookieKeys.has(currentKrookieKey)) {
        setSavedKrookieKeys((prev) => new Set(prev).add(currentKrookieKey));
      }

      for (const krookieKey of krookiesToSubmit) {
        const krookieData = convertedKrookieData.krookies[krookieKey];
        if (!krookieData) continue;

        const { revision, changeNum } = buildRevision(krookieData.historyLog);

        await dispatch(
          updateConvertedKrookieDataAsync({
            id: krookieKey,
            data: {
              revision: revision,
            },
          }),
        );

        await changeKrookieStatus(KrookieStatus.NeedApproval, [
          {
            krookieId: krookieData.id.toString(),
            changeNum,
          },
        ]);

        await dispatch(
          fetchKrookieData({
            sessionId: krookieData.sessionId,
            status: KrookieStatusLabels[KrookieStatus.NeedApproval],
            krookieId: krookieData.id,
          }),
        );
      }

      clearKrookieStates();
      dispatch(fetchKrookies());
    } catch (error) {
      console.error(error);
    }
  };

  const handleRejectKrookie = async () => {
    if (!convertedKrookieData) return;
    try {
      for (const krookieKey of allKrookieKeys) {
        const krookieData = convertedKrookieData.krookies[krookieKey];

        const { revision, changeNum, currentDate } = buildRevision(
          krookieData.historyLog,
        );
        await dispatch(
          updateConvertedKrookieDataAsync({
            id: krookieKey,
            data: {
              revision: revision,
              signatureDrafter: currentDate,
            },
          }),
        );

        await changeKrookieStatus(KrookieStatus.Rejected, [
          {
            krookieId: krookieData.id.toString(),
            changeNum,
          },
        ]);
        await dispatch(
          fetchKrookieData({
            sessionId: krookieData.sessionId,
            status: KrookieStatusLabels[KrookieStatus.Rejected],
            krookieId: krookieData.id,
          }),
        );
      }

      clearKrookieStates();
      dispatch(fetchKrookies());
      if (setVisibleModalRejectKrookie) {
        setVisibleModalRejectKrookie(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseDocument = () => {
    if (onToggleCloseMasterPlanModal) {
      onToggleCloseMasterPlanModal();
    } else {
      setVisibleDocumentKrookie(false);
    }

    if (origin === DocumentOrigin.Journal) {
      dispatch(setResetKrookieData());
    }
  };

  const navigateKrookie = (direction: "next" | "prev") => {
    setCurrentKrookieIndex((prevIndex) => {
      if (direction === "next") {
        return (prevIndex + 1) % allKrookies.length;
      } else {
        return (prevIndex - 1 + allKrookies.length) % allKrookies.length;
      }
    });
  };

  return (
    <div className={styles.modalOverlay}>
      <div key={currentKrookieKey} className={styles.document}>
        <div className={styles.header}>
          <div className={styles.title}>
            <span>
              {t("document-krookie.krookiePlotNumber")}:{" "}
              {origin === DocumentOrigin.Journal
                ? currentKrookie?.plotNumber
                : currentKrookie?.plotNumber}
            </span>
            <p>
              <img
                className={styles.statusImg}
                src={getStatusIcon(status)}
                alt={KrookieStatusLabels[status]}
              />{" "}
              {KrookieStatusLabels[status]}
            </p>
            {hasMultipleKrookies && (
              <span className={styles.countKrookies}>
                {t("document-krookie.krookieCounter", {
                  current: currentKrookieIndex + 1,
                  total: allKrookies.length,
                })}
              </span>
            )}
          </div>
          <img
            className={styles.closeIcon}
            onClick={handleCloseDocument}
            src={closeIcon}
            alt="close icon"
          />
        </div>
        <div className={styles.body}>
          <div className={styles.cardKrookie}>
            {currentDocumentUrl ? (
              <img
                className={styles.documentImage}
                src={currentDocumentUrl}
                alt="Document Krookie"
              />
            ) : (
              <div ref={pdfContainerRef}>
                <CardKrookie data={currentKrookie} />
              </div>
            )}
          </div>
          <div className={styles.rightColumn}>
            <HistoryLog
              documentId={Number(currentKrookieKey)}
              origin={origin}
            />
            {showApprovalAlert && (
              <div className={styles.approvalAlert}>
                <img src={warningIcon} alt="alert" />
                <span>{t("document-krookie.approvalWarning")}</span>
              </div>
            )}
            <KrookieActionPanel
              origin={origin}
              status={status}
              onEdit={handleEditKrookie}
              onApprove={handleApproveKrookie}
              onReject={handleRejectKrookie}
              onSubmitForApproval={handleSubmitForApproval}
              onReturn={handleReturnBackToJournal}
              onPrint={handlePrintKrookie}
              onSave={handleSaveKrookie}
              convertedKrookieData={currentConvertedKrookieData}
              krookieFormationTypeId={krookieFormationTypeId}
              onSaveAndSeeNext={handleSaveKrookieAndSeeNext}
              hasMultipleKrookies={hasMultipleKrookies}
              isLastUnsavedKrookie={isLastUnsavedKrookie}
              onSubmitForApprovalAndSeeNext={handleSubmitForApprovalAndSeeNext}
              onApproveAndSeeNext={handleApproveKrookieAndSeeNext}
            />
          </div>
        </div>
        {hasMultipleKrookies && (
          <>
            <button
              className={`${styles.navButton} ${styles.prevBtn}`}
              onClick={() => navigateKrookie("prev")}
              type="button"
            >
              <span className={styles.img} />
            </button>
            <button
              className={`${styles.navButton} ${styles.nextBtn}`}
              onClick={() => navigateKrookie("next")}
              type="button"
            >
              <span className={styles.img} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentKrookie;
